<script setup>
    import { ref, onMounted, defineProps } from 'vue';
    import { db } from '@/firebase';
    import { getDocs, updateDoc, query, where, collection } from 'firebase/firestore';
    import GenericTable2 from '@/components/tables/GenericTable2.vue';
    import Data from '@/api/data';
    import { utcToLocal } from '@/tools/TimeFunctions';
    const data = ref([]);
    const loading = ref(true);
    const pausedData = ref([]);
    const selectedRows = ref([]);
    const unitRef = ref(null);

    const props = defineProps({
        dataID: {
            type: String,
            required: true
        }
    });

    onMounted(async () => {
        // Get the paused data from the database
        const unitsRef = collection(db, "units");
        const q = query(unitsRef, where("dataID", "==", props.dataID));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            console.error("No units found");
            return;
        }

        unitRef.value = querySnapshot.docs[0].ref;
        const doc = querySnapshot.docs[0].data();
        pausedData.value = doc.pausedData;

        // get the collections from the dataID device
        const response = await Data.get(props.dataID);

        for (const collection of Object.keys(response.body.details)) {
            const collectionData = {}

            // Add the collection name to the collection data
            collectionData["Data Collection"] = collection;

            // Get the last Updated timestamp
            collectionData["Last Updated"] = utcToLocal(response.body.details[collection].lastUpdateUTC);

            // If the collection is paused, add the paused data to the collection data
            if (pausedData.value.includes(collection)) {
                collectionData["Status"] = "Paused";
            } else {
                collectionData["Status"] = "Running";
            }

            // Add the collection data to the data array
            data.value.push(collectionData);
        }
        loading.value = false;
    });

    const pauseData = async () => {
        // Create a new array with all current paused data
        let updatedPausedData = [...pausedData.value];
        
        // Add selected collections that aren't already paused
        for (const row of selectedRows.value) {
            const collection = data.value[row]["Data Collection"];
            if (!updatedPausedData.includes(collection)) {
                updatedPausedData.push(collection);
                // Update UI status immediately
                data.value[row]["Status"] = "Paused";
            }
        }
        
        // Update Firestore with the new array
        await updateDoc(unitRef.value, {
            pausedData: updatedPausedData
        });
        
        // Update local state
        pausedData.value = updatedPausedData;
    }

    const resumeData = async () => {
        // Create a new array with all current paused data
        let updatedPausedData = [...pausedData.value];
        
        // Remove selected collections from the paused data
        for (const row of selectedRows.value) {
            const collection = data.value[row]["Data Collection"];
            updatedPausedData = updatedPausedData.filter(item => item !== collection);
            // Update UI status immediately
            data.value[row]["Status"] = "Running";
        }
        
        // Update Firestore with the new array
        await updateDoc(unitRef.value, {
            pausedData: updatedPausedData
        });
        
        // Update local state
        pausedData.value = updatedPausedData;
    }
    
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-row justify-between items-end">
            <h1 class="text-2xl font-bold underline ml-3 mt-3">Data Control Panel</h1>
            <div class="flex flex-row gap-2 mr-3">
                <button :class="selectedRows.length === 0 ? 'default-button-off' : 'default-button'" :disabled="selectedRows.length === 0" @click="pauseData">Pause</button>
                <button :class="selectedRows.length === 0 ? 'default-button-off' : 'default-button'" :disabled="selectedRows.length === 0" @click="resumeData">Resume</button>
            </div>
        </div>
        <GenericTable2
            :data="data"
            :loading="loading"
            :selectable="true"
            @RowsSelected="selectedRows = $event"
        />
    </div>
</template>